/**
 * sortableTable — pure helpers backing the SortableTable component
 * (PR F · §F.1.2).
 *
 * Kept separate from the React layer so consumers can sort + filter
 * outside the table (e.g. when piping rows to an export, or testing
 * a family lens's table logic without rendering).
 */

export type SortDirection = 'asc' | 'desc';

export interface SortColumnSpec<Row> {
  /** Stable key (string) used by the UI to track active sort. */
  key: string;
  /** Pulls the sortable value out of a row. */
  accessor: (row: Row) => string | number;
  /**
   * Optional custom comparator. When supplied, replaces the default
   * (numeric for numbers, locale-aware for strings). Direction is
   * applied AFTER this returns; supply ascending order from your fn.
   */
  sortFn?: (a: Row, b: Row) => number;
}

/** Return a sorted copy of `rows`. Stable, non-mutating. */
export function sortRows<Row>(
  rows: readonly Row[],
  spec: SortColumnSpec<Row>,
  dir: SortDirection,
): Row[] {
  const out = [...rows];
  const sign = dir === 'asc' ? 1 : -1;
  out.sort((a, b) => {
    if (spec.sortFn) return spec.sortFn(a, b) * sign;
    const av = spec.accessor(a);
    const bv = spec.accessor(b);
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * sign;
    }
    return String(av).localeCompare(String(bv)) * sign;
  });
  return out;
}

/**
 * Return rows passing every predicate (AND semantics). Empty predicate
 * list ⇒ identity. Stable: preserves input order.
 */
export function filterRows<Row>(
  rows: readonly Row[],
  predicates: ReadonlyArray<(row: Row) => boolean>,
): Row[] {
  if (predicates.length === 0) return [...rows];
  return rows.filter((r) => predicates.every((p) => p(r)));
}
